import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView, View, Text, StyleSheet, Image, Platform, TouchableOpacity } from 'react-native';
import { databaseService } from '../../services/databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import { snapshotDbCounts } from '@/utils/test/snapshotDb';
import { TestLogger } from '@/utils/test/testLogger';
import { ChecklistActionsHelper } from '@/services/checklist/ChecklistActionsHelper';
import { ChecklistTemplateCleanupHelper } from '@/services/checklist/ChecklistTemplateCleanupHelper';
import { processPendingDeletions } from '@/utils/file/uploadImageHelper';
import { supabase } from '@/services/supabase';
import { isRecoveryRuntimeBlocked } from '@/services/recovery/RecoveryRuntimeGate';

interface TableData {
  name: string;
  rows: any[];
}

export default function DebugScreen() {
  const [tables, setTables] = useState<TableData[]>([]);
  const [expanded, setExpanded] = useState<{ [name: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [offline, setOffline] = useState(false);
  const [lastLog, setLastLog] = useState<any | null>(null);
  const seedRef = useRef<{ empresaId: string; templateId: string; checklistIds: string[] } | null>(null);
  const originalFromRef = useRef<any | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const tables = APP_CONSTANTS.DATABASE.TABLES as any;
      const allDefs: any[] = Object.values(tables);
      const byName = new Map(allDefs.map((d) => [d.NAME, d]));
      const preferredOrder = [
        'pending_images',
        'pending_deletions',
        'pending_operations',
        'checklist_photos',
        'checklist_signatures',
        'checklist_marcacoes',
        'checklist_marcacao_bkg_images',
        'guincho_marcacao_imagens',
        'guincho_valores_checklist',
        'guincho_checklists',
      ];
      const seen = new Set<string>();
      const orderedDefs: any[] = [];
      for (const name of preferredOrder) {
        const def = byName.get(name);
        if (def) {
          orderedDefs.push(def);
          seen.add(name);
        }
      }
      for (const def of allDefs) {
        if (!seen.has(def.NAME)) {
          orderedDefs.push(def);
        }
      }

      const result: TableData[] = [];
      for (const def of orderedDefs) {
        try {
          let rows: any[] = await databaseService.listItems(def.NAME);
          if (
            Platform.OS === 'web' &&
            def.NAME === APP_CONSTANTS.DATABASE.TABLES.PENDING_IMAGES.NAME
          ) {
            const enhanced: any[] = [];
            for (const row of rows) {
              const typedRow = row as any;
              if (typedRow.local_uri) {
                let imgRow: { base64?: string } | null = null;
                if (typedRow.local_uri.startsWith('checklist-photos')) {
                  imgRow = await databaseService.readItem(
                    APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_PHOTOS.NAME,
                    typedRow.local_uri
                  );
                } else if (typedRow.local_uri.startsWith('checklist-signatures')) {
                  imgRow = await databaseService.readItem(
                    APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_SIGNATURES.NAME,
                    typedRow.local_uri
                  );
                } else if (typedRow.local_uri.startsWith('checklist-marcacoes')) {
                  imgRow = await databaseService.readItem(
                    APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACOES.NAME,
                    typedRow.local_uri
                  );
                }
                enhanced.push({ ...typedRow, _base64: imgRow?.base64 || null });
              } else {
                enhanced.push(typedRow);
              }
            }
            rows = enhanced;
          }
          result.push({ name: def.NAME, rows });
        } catch (e) {
          result.push({ name: def.NAME, rows: [{ error: String(e) }] });
        }
      }
      setTables(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // --- Camada 3: Simulação de offline (wrap do storage.remove)
  useEffect(() => {
    if (!originalFromRef.current) {
      originalFromRef.current = supabase.storage.from.bind(supabase.storage);
    }
    // Reatribui .from com wrapper que intercepta remove
    supabase.storage.from = ((bucket: string) => {
      const base = originalFromRef.current(bucket);
      const wrapped = {
        ...base,
        remove: async (paths: string[]) => {
          if (offline) {
            return { data: null, error: { message: 'offline' } } as any;
          }
          return base.remove(paths);
        },
      };
      return wrapped as any;
    }) as any;
    return () => {
      if (originalFromRef.current) {
        supabase.storage.from = originalFromRef.current;
      }
    };
  }, [offline]);

  const toggleOffline = useCallback(() => setOffline((v) => !v), []);

  // --- Camada 3: util de exportação de JSON
  const exportJson = useCallback(async (obj: any, fileName: string) => {
    const content = JSON.stringify(obj, null, 2);
    if (Platform.OS === 'web') {
      // Importa apenas no web para evitar problemas em mobile
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { saveAs } = require('file-saver');
      const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
      saveAs(blob, fileName);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const FileSystem = require('expo-file-system/legacy');
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const Sharing = require('expo-sharing');
      const dir = FileSystem.documentDirectory + 'test-logs/';
      try { await FileSystem.makeDirectoryAsync(dir, { intermediates: true }); } catch {}
      const path = dir + fileName;
      await FileSystem.writeAsStringAsync(path, content, { encoding: FileSystem.EncodingType.UTF8 });
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(path);
      }
    }
  }, []);

  // --- Camada 3: seed de dados (web + mobile)
  const seedData = useCallback(async () => {
    if (isRecoveryRuntimeBlocked()) {
      setLastLog({ error: 'Seed bloqueado pelo gate de recuperação.' });
      return;
    }

    const T = APP_CONSTANTS.DATABASE.TABLES;
    // Garante init (idempotente)
    await databaseService.initialize('debug-user');
    // Limpa DB para cenário reprodutível
    await databaseService.clearAllData();

    const empresaId = 'emp-debug-1';
    const templateId = 'tpl-debug-1';
    const chk1 = 'chk-debug-1';
    const chk2 = 'chk-debug-2';

    // Empresa e Template
    await databaseService.createItem(T.EMPRESAS.NAME, { id: empresaId, nome: 'Empresa Debug', created_at: Date.now(), updated_at: Date.now() }, true);
    await databaseService.createItem(T.TEMPLATES_CHECKLIST.NAME, { id: templateId, empresa_id: empresaId, nome: 'Template Debug', versao: 1 }, true);

    // Checklists
    const baseChecklist = { empresa_id: empresaId, template_id: templateId, versao: 1, status: 'draft', data_criacao: Date.now(), created_at: Date.now(), updated_at: Date.now() };
    await databaseService.createItem(T.GUINCHO_CHECKLISTS.NAME, { id: chk1, ...baseChecklist } as any, true);
    await databaseService.createItem(T.GUINCHO_CHECKLISTS.NAME, { id: chk2, ...baseChecklist } as any, true);

    // Campos mínimos
    const campoFoto = 'campo-foto-1';
    await databaseService.createItem(T.GUINCHO_CAMPOS_TEMPLATE.NAME, { id: campoFoto, empresa_id: empresaId, template_id: templateId, versao: 1, nome: 'Foto', tipo: 'foto' } as any, true);
    const campoMarc = 'campo-marc-1';
    await databaseService.createItem(T.GUINCHO_CAMPOS_TEMPLATE.NAME, { id: campoMarc, empresa_id: empresaId, template_id: templateId, versao: 1, nome: 'Marca', tipo: 'marcacao' } as any, true);

    // Valores com URLs remotas (para gerar pendências REMOTE quando offline)
    const remotePhoto1 = `https://xxx.supabase.co/storage/v1/object/public/${APP_CONSTANTS.STORAGE.BUCKET.CHECKLIST}/${APP_CONSTANTS.STORAGE.FOLDER.CHECKLIST_PHOTOS}/${empresaId}/${chk1}-1.jpg`;
    const remotePhoto2 = `https://xxx.supabase.co/storage/v1/object/public/${APP_CONSTANTS.STORAGE.BUCKET.CHECKLIST}/${APP_CONSTANTS.STORAGE.FOLDER.CHECKLIST_PHOTOS}/${empresaId}/${chk2}-1.jpg`;
    await databaseService.bulkUpsertChecklistValues(empresaId, chk1, { [campoFoto]: remotePhoto1, [campoMarc]: `checklist-marcacoes/${empresaId}/${chk1}-mk.png` }, { skipPendingOp: true });
    await databaseService.bulkUpsertChecklistValues(empresaId, chk2, { [campoFoto]: remotePhoto2 }, { skipPendingOp: true });

    // Tabelas locais (web) – imagens simuladas
    if (Platform.OS === 'web') {
      await databaseService.createItem(T.CHECKLIST_PHOTOS.NAME, { id: `${empresaId}/${chk1}-1.jpg`, base64: 'AAA', created_at: Date.now(), updated_at: Date.now() } as any, true);
      await databaseService.createItem(T.CHECKLIST_MARCACOES.NAME, { id: `${empresaId}/${chk1}-mk.png`, base64: 'BBB', created_at: Date.now(), updated_at: Date.now() } as any, true);
      await databaseService.createItem(T.CHECKLIST_PHOTOS.NAME, { id: `${empresaId}/${chk2}-1.jpg`, base64: 'CCC', created_at: Date.now(), updated_at: Date.now() } as any, true);
    }

    // Background de marcação (template)
    const bkgUrl = `https://xxx.supabase.co/storage/v1/object/public/${APP_CONSTANTS.STORAGE.BUCKET.CHECKLIST}/${APP_CONSTANTS.STORAGE.FOLDER.CHECKLIST_MARCACAO_BKG}/${empresaId}/${templateId}-${campoMarc}.jpg`;
    await databaseService.createItem(T.GUINCHO_MARCACAO_IMAGENS.NAME, { id: 'bkg-1', empresa_id: empresaId, template_id: templateId, campo_id: campoMarc, foto_url: bkgUrl, created_at: Date.now(), updated_at: Date.now() } as any, true);
    if (Platform.OS === 'web') {
      await databaseService.createItem(T.CHECKLIST_MARCACAO_BKG_IMAGES.NAME, { id: 'bkg-1', empresa_id: empresaId, template_id: templateId, campo_id: campoMarc, foto_url: 'bkg-1', base64: 'BASE64', created_at: Date.now(), updated_at: Date.now() } as any, true);
    }

    seedRef.current = { empresaId, templateId, checklistIds: [chk1, chk2] };
    const snap = await snapshotDbCounts();
    return { ok: true, snap };
  }, []);

  // --- Camada 3: snapshot com breakdown de pending_operations
  const snapshotWithPendingOps = useCallback(async () => {
    const base = await snapshotDbCounts();
    let pendingOps: any[] = [];
    try {
      pendingOps = await databaseService.listItems<any>('pending_operations');
    } catch {}
    const byCollection = pendingOps.reduce((acc: Record<string, number>, op: any) => {
      const coll = op.collection || 'unknown';
      acc[coll] = (acc[coll] || 0) + 1;
      return acc;
    }, {});
    return { ...base, pending_operations: { total: pendingOps.length, byCollection } };
  }, []);

  // --- Camada 3: cenários
  const runChecklistDeleteOffline = useCallback(async () => {
    const seed = seedRef.current;
    if (!seed) return;
    const logger = new TestLogger('checklist_delete_offline', Platform.OS);
    try {
      logger.step('seed', true, { dbSnapshot: await snapshotWithPendingOps() });

      setOffline(true);
      await ChecklistActionsHelper.deleteMany([seed.checklistIds[0]], { silent: true });
      const midSnap = await snapshotWithPendingOps();
      logger.step('delete_offline', true, { dbSnapshot: midSnap });

      setOffline(false);
      await processPendingDeletions();
      const finalSnap = await snapshotWithPendingOps();
      logger.step('online_process_pendings', true, { dbSnapshot: finalSnap });

      const json = logger.toJSON();
      setLastLog(json);
      await exportJson(json, `checklist-delete-offline-${Date.now()}.json`);
    } catch (e) {
      logger.step('error', false);
      setLastLog(logger.toJSON());
    }
  }, [exportJson, snapshotWithPendingOps]);

  const runBulkDeleteOffline = useCallback(async () => {
    const seed = seedRef.current;
    if (!seed) return;
    const logger = new TestLogger('bulk_delete_offline', Platform.OS);
    try {
      logger.step('seed', true, { dbSnapshot: await snapshotWithPendingOps() });

      setOffline(true);
      await ChecklistActionsHelper.deleteMany(seed.checklistIds, { silent: true });
      const midSnap = await snapshotWithPendingOps();
      logger.step('bulk_delete_offline', true, { dbSnapshot: midSnap });

      setOffline(false);
      await processPendingDeletions();
      const finalSnap = await snapshotWithPendingOps();
      logger.step('online_process_pendings', true, { dbSnapshot: finalSnap });

      const json = logger.toJSON();
      setLastLog(json);
      await exportJson(json, `bulk-delete-offline-${Date.now()}.json`);
    } catch (e) {
      logger.step('error', false);
      setLastLog(logger.toJSON());
    }
  }, [exportJson, snapshotWithPendingOps]);

  const runTemplateDeleteOffline = useCallback(async () => {
    const seed = seedRef.current;
    if (!seed) return;
    const logger = new TestLogger('template_delete_offline', Platform.OS);
    try {
      logger.step('seed', true, { dbSnapshot: await snapshotWithPendingOps() });

      setOffline(true);
      const helper = new ChecklistTemplateCleanupHelper();
      const removed = await helper.cleanupCompleteTemplate(seed.templateId, seed.empresaId);
      const midSnap = await snapshotWithPendingOps();
      logger.step('cleanup_complete_template_offline', true, { removed, dbSnapshot: midSnap });

      setOffline(false);
      await processPendingDeletions();
      const finalSnap = await snapshotWithPendingOps();
      logger.step('online_process_pendings', true, { dbSnapshot: finalSnap });

      const json = logger.toJSON();
      setLastLog(json);
      await exportJson(json, `template-delete-offline-${Date.now()}.json`);
    } catch (e) {
      logger.step('error', false);
      setLastLog(logger.toJSON());
    }
  }, [exportJson, snapshotWithPendingOps]);

  const runAll = useCallback(async () => {
    await seedData();
    await runChecklistDeleteOffline();
    await seedData();
    await runBulkDeleteOffline();
    await seedData();
    await runTemplateDeleteOffline();
  }, [runChecklistDeleteOffline, runBulkDeleteOffline, runTemplateDeleteOffline, seedData]);

  const isPhotoTable = (table: string) => {
    return table === APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_PHOTOS.NAME;
  };

  const isPendingImages = (table: string) => {
    return table === APP_CONSTANTS.DATABASE.TABLES.PENDING_IMAGES.NAME;
  };

  return (
    <ScrollView style={styles.container}>
      {/* Camada 3: Test Runner */}
      <View style={styles.runnerCard}>
        <Text style={styles.runnerTitle}>Test Runner (Camada 3)</Text>
        <View style={styles.rowH}>
          <TouchableOpacity onPress={async () => { const res = await seedData(); setLastLog({ scenario: 'seed_only', ...res }); }} style={styles.button}>
            <Text style={styles.buttonText}>Seed data</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={toggleOffline} style={[styles.button, offline && styles.buttonActive]}>
            <Text style={styles.buttonText}>{offline ? 'Simulando offline: ON' : 'Simular offline: OFF'}</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={runAll} style={[styles.button, styles.buttonPrimary]}>
            <Text style={styles.buttonText}>Run all</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.rowH}>
          <TouchableOpacity onPress={runChecklistDeleteOffline} style={styles.button}>
            <Text style={styles.buttonText}>Checklist delete (offline)</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={runBulkDeleteOffline} style={styles.button}>
            <Text style={styles.buttonText}>Bulk delete (offline)</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={runTemplateDeleteOffline} style={styles.button}>
            <Text style={styles.buttonText}>Template delete (offline)</Text>
          </TouchableOpacity>
        </View>
        {lastLog && (
          <View style={styles.logBox}>
            <Text style={styles.logTitle}>Último log</Text>
            <Text style={styles.logText}>{JSON.stringify(lastLog, null, 2)}</Text>
            <TouchableOpacity onPress={() => exportJson(lastLog, `last-log-${Date.now()}.json`)} style={styles.button}>
              <Text style={styles.buttonText}>Exportar JSON</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Botão de atualizar */}
      <TouchableOpacity
        onPress={loadData}
        style={[styles.refreshButton, loading && styles.refreshButtonDisabled]}
        disabled={loading}
      >
        <Text style={styles.refreshButtonText}>
          {loading ? 'Atualizando...' : '🔄 Atualizar'}
        </Text>
      </TouchableOpacity>

      {tables.map(({ name, rows }) => (
        <View key={name} style={styles.section}>
          <TouchableOpacity
            onPress={() => setExpanded((prev) => ({ ...prev, [name]: !prev[name] }))}
            style={styles.expandBtn}
          >
            <Text style={styles.title}>
              {expanded[name] ? '▼' : '►'} {name} ({rows.length})
            </Text>
          </TouchableOpacity>
          {expanded[name] && (
            rows.length === 0 ? (
              <Text style={styles.empty}>Nenhum registro</Text>
            ) : (
              rows.map((row, idx) => (
                <View key={idx} style={styles.row}>
                  {(isPhotoTable(name) && row.base64) && (
                    <Image source={{ uri: `data:image/*;base64,${row.base64}` }} style={styles.img} />
                  )}
                  {isPendingImages(name) && row.local_uri && (
                    Platform.OS === 'web' ? (
                      row._base64 ? (
                        <Image source={{ uri: `data:image/*;base64,${row._base64}` }} style={styles.img} />
                      ) : (
                        <Text style={styles.missing}>Imagem não encontrada</Text>
                      )
                    ) : (
                      <Image source={{ uri: row.local_uri }} style={styles.img} />
                    )
                  )}
                  <Text style={styles.rowText}>{JSON.stringify(row)}</Text>
                </View>
              ))
            )
          )}
        </View>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#fff',
  },
  runnerCard: {
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    marginBottom: 16,
  },
  runnerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  rowH: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 8,
    flexWrap: 'wrap',
  },
  button: {
    backgroundColor: '#374151',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  buttonPrimary: {
    backgroundColor: '#2563eb',
  },
  buttonActive: {
    backgroundColor: '#B91C1C',
  },
  buttonText: {
    color: '#fff',
    fontWeight: '600',
  },
  refreshButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginBottom: 20,
    alignItems: 'center',
  },
  refreshButtonDisabled: {
    backgroundColor: '#999',
  },
  refreshButtonText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
  },
  section: {
    marginBottom: 24,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 8,
    fontSize: 16,
  },
  empty: {
    color: '#666',
  },
  row: {
    marginBottom: 8,
  },
  rowText: {
    fontSize: 12,
    color: '#333',
  },
  logBox: {
    marginTop: 8,
    padding: 8,
    backgroundColor: '#fff',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  logTitle: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  logText: {
    fontSize: 12,
    color: '#111827',
  },
  img: {
    width: 80,
    height: 80,
    marginBottom: 4,
  },
  missing: {
    fontStyle: 'italic',
    color: '#900',
    marginBottom: 4,
  },
  expandBtn: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginBottom: 4,
    backgroundColor: '#f0f0f0',
    borderRadius: 4,
  },
});
